import {
  Accordion,
  AccordionSummary,
  Box,
  BoxProps,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
} from "@mui/material";
import { FC, ReactNode, useEffect, useState } from "react";
import {
  useLocation,
  useMatch,
  useNavigate,
  useRouteLoaderData,
} from "react-router-dom";
import { TUser } from "../../services/auth";
import { LetterAvatar } from "../LetterAvatar";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { REPORT_TYPE } from "../../constants/dashboard";

export type TMenuItem = {
  label?: string | ReactNode;
  icon?: ReactNode | null;
  link?: string;
  children: child[];
  onClick?: () => void;
  divider?: boolean;
  group?: boolean;
  selected?: boolean;
};

interface child {
  title: string;
  icon: ReactNode | null;
  link: string;
  onClick?: () => void;
}

interface Props extends BoxProps {
  dashboardList?: { DashboardId: string; Name: string }[];
  items?: TMenuItem[];
  isMimi?: boolean;
  isMobile?: boolean;
}

export const NavMenu: FC<Props> = ({
  items,
  dashboardList,
  isMimi,
  isMobile,
  sx,
  ...props
}) => {
  const { user } = useRouteLoaderData("root") as { user: TUser };

  return (
    <Box
      sx={{
        overflow: "auto",
        height: "100%",
        backgroundColor: "#006464",
        ...sx,
      }}
      {...props}
    >
      <List>
        {isMobile && (
          <>
            <ListItem sx={{ pl: isMimi ? 1.5 : undefined, pb: 2 }}>
              <ListItemAvatar>
                <LetterAvatar
                  src={user?.avatar}
                  name={user?.name || user?.email}
                  size={40}
                />
              </ListItemAvatar>
              <ListItemText primary={user?.name} />
            </ListItem>
            <Divider />
          </>
        )}
        {items?.map((item, index) => (
          <NavMenuItem
            key={index}
            item={item}
            isMimi={isMimi}
            dashboardList={dashboardList}
          />
        ))}
      </List>
    </Box>
  );
};

const NavMenuItem: FC<{
  item: TMenuItem;
  isMimi?: boolean;
  dashboardList?: { DashboardId: string; Name: string }[];
}> = ({ item, isMimi, dashboardList }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const match = useMatch({ path: item.link || "/", end: item.link === "/" });
  const isActive = Boolean(item.link && match);
  const firstLogin =
    !location.pathname.includes("report") &&
    !location.pathname.includes("message");
  const [isOpne, setIsOpne] = useState(true);
  const [accordionLink, setAccordionLink] = useState("");
  const [accordionIndex, setAccordionIndex] = useState(0);
  const [accordionOpenStatus, setAccordionOpenStatus] = useState(true);

  useEffect(() => {
    if (location.pathname.includes("message")) {
      setDefaultColor("message_button");
    } else if (location.pathname.includes("device")) {
      setDefaultColor("device_button");
    }
  }, [location.pathname]);

  const setDefaultAccordionPage = () => {
    setAccordionLink("/device");
    navigate("/device", { state: dashboardList });
  };
  const clearDashboardList = () => {
    const existingList = document.getElementById("dashboard-select");
    if (existingList && existingList.innerHTML !== "") {
      existingList.innerHTML = "";
    }
  };
  const clearDashBoard = () => {
    const existingDashboard = document.getElementById("dashboardContainer");
    if (existingDashboard && existingDashboard.innerHTML !== "") {
      existingDashboard.innerHTML = "";
    }
  };

  const setDefaultColor = (type: string) => {
    const existingButton = document.getElementById(type);
    if (existingButton && !existingButton.getAttribute("style")) {
      existingButton.setAttribute("style", "background-color: #009696");
    }
  };

  const clearDefaultColor = (clearAll: boolean, type?: string) => {
    if (clearAll) {
      const existingMessageButton = document.getElementById("message_button");
      const existingDeviceButton = document.getElementById("device_button");
      if (
        existingMessageButton &&
        existingMessageButton.getAttribute("style")
      ) {
        existingMessageButton?.removeAttribute("style");
      }
      if (existingDeviceButton && existingDeviceButton.getAttribute("style")) {
        existingDeviceButton?.removeAttribute("style");
      }
    } else if (type) {
      const existingButton = document.getElementById(type);
      if (existingButton && existingButton.getAttribute("style")) {
        existingButton?.removeAttribute("style");
      }
    }
  };

  useEffect(() => {
    if (location.pathname.includes(REPORT_TYPE.STEPS)) {
      setAccordionIndex(1);
    } else if (location.pathname.includes(REPORT_TYPE.GNSS)) {
      setAccordionIndex(3);
    } else if (location.pathname.includes(REPORT_TYPE.ALARM)) {
      setAccordionIndex(2);
    } else {
      setAccordionIndex(0);
    }
  }, [location.pathname]);

  const listItem = (
    <ListItem disablePadding sx={{ backgroundColor: "#006464" }}>
      {item.label != "Report" && (
        <ListItemButton
          id={
            item.label === "Send Message" ? "message_button" : "device_button"
          }
          selected={isActive}
          sx={{
            color: "white",
            height: item.label === "Send Message" ? 52 : 65,
            marginTop: item.label === "Send Message" ? "" : "-13px"
          }}
          onClick={() => {
            item.onClick
              ? item.onClick()
              : item.link
                ? navigate(item.link)
                : undefined;
            setDefaultColor(
              item.label === "Send Message"
                ? "message_button"
                : "device_button",
            );
            setAccordionOpenStatus(false);
            clearDefaultColor(
              false,
              item.label === "Send Message"
                ? "device_button"
                : "message_button",
            );
          }}
        >
          {item.icon && (
            <ListItemIcon sx={{ color: "inherit", minWidth: 44 }}>
              {item.icon}
            </ListItemIcon>
          )}
          {item.label && <ListItemText primary={item.label} />}
        </ListItemButton>
      )}
      {item.label == "Report" && (
        <Accordion
          defaultExpanded={accordionOpenStatus}
          onChange={() => {
            setAccordionOpenStatus(true);
          }}
          sx={{
            width: "inherit",
            borderRadius: "unset !important",
            marginTop: "-7px !important",
          }}
        >
          <AccordionSummary
            onClick={() => {
              accordionIndex == 0 && accordionLink == ""
                ? setDefaultAccordionPage()
                : navigate(accordionLink, { state: dashboardList });
              setAccordionOpenStatus(true);
              setIsOpne(!isOpne);
              clearDefaultColor(true);
            }}
            expandIcon={""}
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{
              color: "white",
              borderBottom: "2px solid rgba(0, 0, 0, 0.5)",
              backgroundColor: "#006464",
              ".MuiAccordionSummary-content": {
                alignItems: "center",
              },
              height: 45,
            }}
          >
            {item.icon && (
              <ListItemIcon sx={{ color: "inherit", minWidth: 44 }}>
                {item.icon}
              </ListItemIcon>
            )}
            {item.label && <ListItemText primary={item.label} />}
            {isOpne ? <ExpandLess /> : <ExpandMore />}
          </AccordionSummary>
          {item.children?.map((child, index) => {
            return (
              <ListItem
                onClick={() => {
                  setAccordionLink(child.link);
                  setAccordionIndex(index);
                  setAccordionOpenStatus(true);
                  clearDashboardList();
                  clearDashBoard();
                  clearDefaultColor(true);
                  navigate(child.link, { state: dashboardList });
                }}
                sx={{
                  color: "white",
                  backgroundColor: () =>
                    ((accordionOpenStatus && isActive) || firstLogin) &&
                    accordionIndex == index &&
                    !(
                      location.pathname.includes("message") ||
                      location.pathname.includes("device")
                    )
                      ? "#009696"
                      : "#006464",
                  paddingLeft: "30px",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "none",
                    backgroundColor:
                      index != accordionIndex || location.pathname.includes("device") ||
                      location.pathname.includes("message")
                        ? "#003232"
                        : "",
                  },
                }}
              >
                {child.icon && (
                  <ListItemIcon sx={{ color: "inherit", minWidth: 44 }}>
                    {child.icon}
                  </ListItemIcon>
                )}
                {child.title && <ListItemText primary={child.title} />}
              </ListItem>
            );
          })}
        </Accordion>
      )}
    </ListItem>
  );

  if (isMimi) {
    if (item.divider || item.group) return null;

    return (
      <Tooltip title={item.label} placement="right" arrow>
        {listItem}
      </Tooltip>
    );
  }

  if (item.divider) return <Divider />;

  if (item.group) return <ListSubheader>{item.label}</ListSubheader>;

  return listItem;
};
